import { useState, useEffect, useMemo, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api, { authApi, saasApi, posApi, privacyApi } from '@/services/api';
import { useAuthStore } from '@/store/authStore';
import { useUIStore } from '@/store/uiStore';

import Sidebar from '@/components/common/Sidebar';
import TrialBanner from '@/components/common/TrialBanner';
import { Settings, Store, Database, Loader2, Save, Trash2, AlertTriangle, CheckCircle, CreditCard, Shield, Clock, Printer, Landmark, Menu, Sliders, FileText, Tag, FileCheck, Cloud, Download, Scale, Package, Mail, Truck, Search, X, ShoppingBag, Megaphone } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import type { PosSettings } from '@/types';
import { isCompanyAdmin } from '@/types';
import { BUSINESS_TYPES, normalizeBusinessType } from '@/constants/businessTypes';
import PrinterSettingsPage from './PrinterSettingsPage';
import ScaleSettingsPage from './ScaleSettingsPage';
import CashPocketsTab from '@/components/settings/CashPocketsTab';
import BanksTab from '@/components/settings/BanksTab';
import CardMachinesTab from '@/components/settings/CardMachinesTab';
import BrandsTab from '@/components/settings/BrandsTab';
import ModelsTab from '@/components/settings/ModelsTab';
import { showBrandModel } from '@/constants/industryFeatures';
import ReceiptTemplateDesigner from '@/components/settings/ReceiptTemplateDesigner';
import FBRIntegrationTab from '@/components/settings/FBRIntegrationTab';
import ZATCAIntegrationTab from '@/components/settings/ZATCAIntegrationTab';
import UAEIntegrationTab from '@/components/settings/UAEIntegrationTab';
import MarketplaceChannelsTab from '@/components/settings/MarketplaceChannelsTab';
import MarketingHubTab from '@/components/settings/MarketingHubTab';
import PosSyncModeTab from '@/components/settings/PosSyncModeTab';
import StarterCatalogTab from '@/components/settings/StarterCatalogTab';
import ClearDataModal from '@/components/settings/ClearDataModal';
import ConfirmModal from '@/components/common/ConfirmModal';
import { getAppBaseDomain } from '@/utils/subdomain';
import {
  normalizePhone,
  sanitizePhoneInput,
  validatePhone,
} from '@/utils/mobileValidation';
import { getApiErrorMessage } from '@/utils/apiError';
import { resolveFileUrl } from '@/utils/fileUrl';
import { useSyncStore } from '@/store/syncStore';
import {
  COUNTRIES,
  CURRENCIES,
  getCountry,
  getRegionsForCountry,
  getAllUiLanguages,
  sortUiLanguagesForPicker,
  getTimezoneOptions,
  getTimezoneForCountry,
  isEuOrUk,
  isLanguageAllowed,
  isPakistan,
  isSaudiArabia,
  isUae,
  isGcc,
  isUk,
  getDefaultVatRate,
  getDefaultPricesIncludeTax,
} from '@/data/locales';
import UkPostcodeLookup from '@/components/common/UkPostcodeLookup';
import { testIdealPostcodesKey } from '@/services/ukPostcode';
import { setAppLanguage } from '@/i18n';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { getCurrencySymbol } from '@/utils/currency';

type SettingsTab = 'company' | 'pos_rules' | 'pos_sync' | 'fbr' | 'zatca' | 'uae' | 'marketplaces' | 'marketing' | 'receipt_designer' | 'notifications' | 'data' | 'subscription' | 'printer' | 'scale' | 'cash_pockets' | 'banks' | 'card_machines' | 'brands' | 'models' | 'privacy' | 'starter_catalog';

interface CityOption {
  name: string;
  city: string;
}



export default function SettingsPage() {
  const { company, role, user } = useAuthStore();
  const { toggleSidebar } = useUIStore();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const VALID_SETTINGS_TABS: SettingsTab[] = [
    'company', 'pos_rules', 'pos_sync', 'fbr', 'zatca', 'uae',
    'marketplaces', 'marketing', 'receipt_designer', 'notifications', 'data', 'privacy', 'subscription',
    'printer', 'scale', 'cash_pockets', 'banks', 'card_machines', 'brands', 'models', 'starter_catalog',
  ];

  const [activeTab, setActiveTab] = useState<SettingsTab>(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab') as SettingsTab;
    if (tab && VALID_SETTINGS_TABS.includes(tab)) return tab;
    return 'company';
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab === 'profile') {
      navigate('/account', { replace: true });
      return;
    }
    if (tab === 'stripe') {
      navigate('/public-store?tab=card_payment', { replace: true });
      return;
    }
    if (tab === 'categories') {
      navigate('/categories', { replace: true });
      return;
    }
    // Shopify / WhatsApp live under Marketplaces now
    if (tab === 'shopify') {
      const q = new URLSearchParams(location.search);
      q.set('tab', 'marketplaces');
      q.set('configure', 'shopify');
      navigate(`/settings?${q.toString()}`, { replace: true });
      return;
    }
    if (tab === 'whatsapp') {
      navigate('/settings?tab=marketplaces&configure=whatsapp', { replace: true });
      return;
    }
    // Keep UI in sync when URL changes (e.g. deep links)
    if (tab && VALID_SETTINGS_TABS.includes(tab as SettingsTab) && tab !== activeTab) {
      setActiveTab(tab as SettingsTab);
    }
  }, [location.search, navigate, activeTab]);

  const qc = useQueryClient();
  const [anonymizeId, setAnonymizeId] = useState('');
  const [privacyBusy, setPrivacyBusy] = useState(false);
  const [showAnonymizeConfirm, setShowAnonymizeConfirm] = useState(false);

  const handleTabChange = (tabId: SettingsTab) => {
    setActiveTab(tabId);
    navigate(`?tab=${tabId}`, { replace: true });
  };

  const syncLocaleContext = (country: string, currency: string, uiLanguage: string) => {
    void import('@/utils/currency').then(({ setLocaleContext }) => {
      setLocaleContext({ country, currency, uiLanguage });
    });
  };

  const { data: profile } = useQuery({
    queryKey: ['profile'],
    queryFn: async () => { const res = await authApi.getCompanyProfile(); return res.data.message; },
  });

  const { data: trialStatus } = useQuery({
    queryKey: ['trialStatus'],
    queryFn: async () => { const res = await saasApi.getTrialStatus(); return res.data.message; },
  });

  const { data: cities = [] } = useQuery({
    queryKey: ['cities'],
    queryFn: async () => {
      const res = await posApi.getCities();
      return res.data.message as CityOption[];
    },
  });

  const [companyForm, setCompanyForm] = useState({
    company_name: '', company_name_ar: '', owner_name: '', email: '', mobile: '', city: '', address: '', business_type: 'Groceries & Supermarket', company_logo: '',
    country: 'GB', currency: 'GBP', province: '', postal_code: '',
    tax_registration_id: '', prices_include_tax: false, ui_language: 'en', timezone: 'Europe/London', privacy_policy_url: '',
    low_stock_alerts: true, daily_sales_summary: true, subscription_expiry_reminder: true, new_invoice_notifications: false,
    enable_ideal_postcodes: true,
    ideal_postcodes_api_key: '',
  });
  const [idealTestMsg, setIdealTestMsg] = useState('');
  const [idealTesting, setIdealTesting] = useState(false);
  const idealKeyDraftRef = useRef('');
  const [posForm, setPosForm] = useState<PosSettings>({
    allow_sell_out_of_stock_item: false,
    max_discount: 0,
    max_round_off: 50,
    show_tax_field_in_pos: true,
    show_tax_in_invoice: true,
    enable_item_tax: false,
    tax_label: 'Tax',
    show_discount_field_in_pos: true,
    show_discount_in_invoice_print: true,
    show_today_invoices_to_cashier: false,
    pos_category_position: 'Top',
    pos_sync_mode: 'Live Online',
    offline_sync_allow_negative_stock: true,
    offline_invoice_prefix: 'OFF',
    offline_bypass_shift_in_local_modes: true,
    use_barcode_or_qr: 'Barcode',
    print_price_on_label: true,
    sell_only_products: false,
    enable_delivery: false,
    delivery_pricing_mode: 'product_price',
    receipt_template_settings: undefined,
    daily_sales_summary: true,
    daily_sales_summary_hour: 20,
    open_drawer_on_cash_payment: true,
    open_drawer_on_cash_movement: false,
    customer_display_enabled: false,
    enforce_shift_time_limit: true,
    max_shift_hours: 24,
    shift_expiry_warning_hours: 22,
    enable_commission: false,
  });
  const [posMessage, setPosMessage] = useState('');
  const [posError, setPosError] = useState('');
  const [posRulesSearch, setPosRulesSearch] = useState('');
  const [posRulesCategory, setPosRulesCategory] = useState<'all' | 'delivery' | 'pricing' | 'inventory' | 'shifts' | 'layout' | 'hardware'>('all');
  const [companyMobileError, setCompanyMobileError] = useState('');
  const [testingEmail, setTestingEmail] = useState(false);

  useEffect(() => {
    if (profile) {
      setCompanyForm({
        company_name: profile.company_name || '', company_name_ar: profile.company_name_ar || '',
        owner_name: profile.owner_name || '',
        email: profile.email || '', mobile: profile.mobile || '', city: profile.city || '',
        address: profile.address || '', business_type: normalizeBusinessType(profile.business_type),
        company_logo: profile.company_logo || '',
        country: profile.country || 'GB',
        currency: profile.currency || 'GBP',
        province: profile.province || '',
        postal_code: profile.postal_code || '',
        tax_registration_id: profile.tax_registration_id || '',
        prices_include_tax:
          profile.prices_include_tax !== undefined
            ? !!profile.prices_include_tax
            : getDefaultPricesIncludeTax(profile.country || 'GB'),
        ui_language: profile.ui_language || 'en',
        timezone: profile.timezone || getTimezoneForCountry(profile.country || 'GB'),
        privacy_policy_url: profile.privacy_policy_url || '',
        low_stock_alerts: profile.low_stock_alerts !== 0,
        daily_sales_summary: profile.daily_sales_summary !== 0,
        subscription_expiry_reminder: profile.subscription_expiry_reminder !== 0,
        new_invoice_notifications: profile.new_invoice_notifications !== 0,
        enable_ideal_postcodes: profile.enable_ideal_postcodes !== 0 && profile.enable_ideal_postcodes !== false,
        // Never echo secrets from the server; keep an in-progress draft across profile refetch
        ideal_postcodes_api_key: idealKeyDraftRef.current,
      });
      // Apply locale context for money formatting
      void import('@/utils/currency').then(({ setLocaleContext }) => {
        setLocaleContext({
          country: profile.country || 'GB',
          currency: profile.currency || 'GBP',
          uiLanguage: profile.ui_language || 'en',
        });
      });
      setAppLanguage(profile.ui_language || 'en', profile.country || 'GB');
      setCompanyMobileError('');
      setPosForm({
        allow_sell_out_of_stock_item: profile.allow_sell_out_of_stock_item ?? false,
        max_discount: profile.max_discount ?? 0,
        max_round_off: profile.max_round_off ?? 50,
        show_tax_field_in_pos: profile.show_tax_field_in_pos ?? true,
        show_tax_in_invoice: profile.show_tax_in_invoice ?? true,
        enable_item_tax: profile.enable_item_tax ?? false,
        tax_label: profile.tax_label || 'Tax',
        show_discount_field_in_pos: profile.show_discount_field_in_pos ?? true,
        show_discount_in_invoice_print: profile.show_discount_in_invoice_print ?? true,
        show_today_invoices_to_cashier: profile.show_today_invoices_to_cashier ?? false,
        pos_category_position: profile.pos_category_position ?? 'Top',
        pos_sync_mode: profile.pos_sync_mode ?? 'Live Online',
        offline_sync_allow_negative_stock: profile.offline_sync_allow_negative_stock ?? true,
        offline_invoice_prefix: profile.offline_invoice_prefix ?? 'OFF',
        offline_bypass_shift_in_local_modes: profile.offline_bypass_shift_in_local_modes ?? true,
        use_barcode_or_qr: profile.use_barcode_or_qr ?? 'Barcode',
        print_price_on_label: profile.print_price_on_label ?? true,
        sell_only_products: profile.sell_only_products ?? false,
        enable_delivery: profile.enable_delivery ?? false,
        delivery_pricing_mode: profile.delivery_pricing_mode || 'product_price',
        receipt_template_settings: profile.receipt_template_settings,
        daily_sales_summary: profile.daily_sales_summary === undefined ? true : !!profile.daily_sales_summary,
        daily_sales_summary_hour:
          typeof profile.daily_sales_summary_hour === 'number' ? profile.daily_sales_summary_hour : 20,
        open_drawer_on_cash_payment: profile.open_drawer_on_cash_payment === undefined ? true : !!profile.open_drawer_on_cash_payment,
        open_drawer_on_cash_movement: !!profile.open_drawer_on_cash_movement,
        customer_display_enabled: !!profile.customer_display_enabled,
        enforce_shift_time_limit: profile.enforce_shift_time_limit === undefined ? true : !!profile.enforce_shift_time_limit,
        max_shift_hours: profile.max_shift_hours ?? 24,
        shift_expiry_warning_hours: profile.shift_expiry_warning_hours ?? 22,
        enable_commission: !!profile.enable_commission,
      });
    }
  }, [profile]);

  const updateCompanyMutation = useMutation({
    mutationFn: (data: unknown) => authApi.updateCompanyProfile(data),
    onSuccess: () => {
      idealKeyDraftRef.current = '';
      setCompanyForm((prev) => ({ ...prev, ideal_postcodes_api_key: '' }));
      qc.invalidateQueries({ queryKey: ['profile'] });
      toast.success(t('settings.companySaved'));
    },
    onError: (err: unknown) => {
      toast.error(getApiErrorMessage(err, t('settings.companySaveFailed')));
    },
  });

  const updatePosSettingsMutation = useMutation({
    mutationFn: (data: PosSettings) => posApi.updatePosSettings(data),
    onSuccess: (res) => {
      const saved = res.data.message as PosSettings & { success?: boolean };
      setPosForm({
        allow_sell_out_of_stock_item: saved.allow_sell_out_of_stock_item ?? false,
        max_discount: saved.max_discount ?? 0,
        max_round_off: saved.max_round_off ?? 50,
        show_tax_field_in_pos: saved.show_tax_field_in_pos ?? true,
        show_tax_in_invoice: saved.show_tax_in_invoice ?? true,
        enable_item_tax: saved.enable_item_tax ?? false,
        tax_label: saved.tax_label || 'Tax',
        show_discount_field_in_pos: saved.show_discount_field_in_pos ?? true,
        show_discount_in_invoice_print: saved.show_discount_in_invoice_print ?? true,
        show_today_invoices_to_cashier: saved.show_today_invoices_to_cashier ?? false,
        pos_category_position: saved.pos_category_position ?? 'Top',
        pos_sync_mode: saved.pos_sync_mode ?? 'Live Online',
        offline_sync_allow_negative_stock: saved.offline_sync_allow_negative_stock ?? true,
        offline_invoice_prefix: saved.offline_invoice_prefix ?? 'OFF',
        offline_bypass_shift_in_local_modes: saved.offline_bypass_shift_in_local_modes ?? true,
        use_barcode_or_qr: saved.use_barcode_or_qr ?? 'Barcode',
        print_price_on_label: saved.print_price_on_label ?? true,
        sell_only_products: saved.sell_only_products ?? false,
        enable_delivery: saved.enable_delivery ?? false,
        delivery_pricing_mode: saved.delivery_pricing_mode || 'product_price',
        receipt_template_settings: saved.receipt_template_settings,
        daily_sales_summary: saved.daily_sales_summary ?? true,
        daily_sales_summary_hour: saved.daily_sales_summary_hour ?? 20,
        open_drawer_on_cash_payment: saved.open_drawer_on_cash_payment === undefined ? true : !!saved.open_drawer_on_cash_payment,
        open_drawer_on_cash_movement: !!saved.open_drawer_on_cash_movement,
        customer_display_enabled: !!saved.customer_display_enabled,
        enforce_shift_time_limit: saved.enforce_shift_time_limit === undefined ? true : !!saved.enforce_shift_time_limit,
        max_shift_hours: saved.max_shift_hours ?? 24,
        shift_expiry_warning_hours: saved.shift_expiry_warning_hours ?? 22,
        enable_commission: !!saved.enable_commission,
      });
      qc.invalidateQueries({ queryKey: ['profile'] });
      qc.invalidateQueries({ queryKey: ['posSettings'] });
      if (saved.pos_sync_mode) {
        useSyncStore.getState().setPosSyncMode(saved.pos_sync_mode);
      }
      const mode = saved.pos_sync_mode || 'Live Online';
      // Moving into Offline Always / Local First: download full catalog in chunks with progress popup
      if (mode === 'Local First Auto Sync' || mode === 'Offline Always Manual Sync') {
        void import('@/offline').then(async ({ runChunkedCatalogSync }) => {
          try {
            await runChunkedCatalogSync({ force: true });
          } catch {
            // Popup shows error; user can retry via Refresh Local Catalog
          }
          qc.invalidateQueries({ queryKey: ['posItems'] });
          qc.invalidateQueries({ queryKey: ['items'] });
          qc.invalidateQueries({ queryKey: ['posCustomers'] });
          qc.invalidateQueries({ queryKey: ['customers'] });
          qc.invalidateQueries({ queryKey: ['posCategories'] });
          qc.invalidateQueries({ queryKey: ['suppliers'] });
        });
      }
      if (activeTab === 'receipt_designer') {
        setPosMessage(t('settings.receiptTemplateSaved', 'Receipt template saved successfully.'));
      } else {
        setPosMessage(t('settings.posSettingsSaved', 'POS settings saved successfully.'));
      }
      setPosError('');
    },
    onError: (err: unknown) => {
      const msg = getApiErrorMessage(err, 'Failed to save POS settings');
      setPosError(msg);
      setPosMessage('');
      // Desktop: soft-flag expired cloud so banner/reconnect appears.
      if (typeof window !== 'undefined' && window.electronAPI) {
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (status === 401 || status === 403) {
          void import('@/desktop/desktopSessionStore').then(({ useDesktopSessionStore }) => {
            useDesktopSessionStore.getState().setCloudSessionExpired(true);
          });
        }
      }
    },
  });

  const loadSampleDataMutation = useMutation({
    mutationFn: () => saasApi.loadSampleData(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['profile'] }),
  });

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const deleteSampleDataMutation = useMutation({
    mutationFn: () => saasApi.deleteSampleData(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['profile'] });
      setShowDeleteConfirm(false);
    },
  });

  const [showClearDataModal, setShowClearDataModal] = useState(false);
  // const [showFactoryResetConfirm, setShowFactoryResetConfirm] = useState(false);
  // const factoryResetMutation = useMutation({
  //   mutationFn: () => saasApi.factoryReset(),
  //   onSuccess: () => {
  //     qc.setQueriesData({ queryKey: ['profile'] }, (old: Record<string, unknown> | undefined) =>
  //       old ? { ...old, is_sample_data_loaded: false } : old
  //     );
  //     qc.setQueriesData({ queryKey: ['trialStatus'] }, (old: Record<string, unknown> | undefined) =>
  //       old ? { ...old, is_sample_data_loaded: false } : old
  //     );
  //     qc.invalidateQueries();
  //     setShowFactoryResetConfirm(false);
  //   },
  // });



  const handleCompanySave = (e: React.FormEvent) => {
    e.preventDefault();
    const mobileErr = validatePhone(companyForm.mobile, companyForm.country, { required: true });
    if (mobileErr) {
      setCompanyMobileError(mobileErr);
      return;
    }
    setCompanyMobileError('');
    let lang = companyForm.ui_language;
    if (!isLanguageAllowed(null, lang)) lang = 'en';
    const { country: _country, currency: _currency, business_type: _businessType, ...editableCompany } = companyForm;
    updateCompanyMutation.mutate({
      ...editableCompany,
      ui_language: lang,
      mobile: normalizePhone(companyForm.mobile, companyForm.country),
      prices_include_tax: companyForm.prices_include_tax ? 1 : 0,
      enable_ideal_postcodes: companyForm.enable_ideal_postcodes ? 1 : 0,
      enable_fbr_integration: companyForm.country === 'PK' ? undefined : 0,
      enable_zatca_integration: companyForm.country === 'SA' ? undefined : 0,
    });
  };

  const handlePosSettingsSave = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setPosMessage('');
    setPosError('');
    updatePosSettingsMutation.mutate(posForm);
  };

  const isCompanyAdminUser = isCompanyAdmin(role);
  const settingsCountry = companyForm.country || profile?.country || 'GB';

  const companyLanguageOptions = useMemo(() => {
    const withLabels = getAllUiLanguages().map((l) => ({
      ...l,
      label: t(`languages.${l.code}`, { defaultValue: l.label }),
    }));
    return sortUiLanguagesForPicker(withLabels, {
      selectedCode: companyForm.ui_language,
      countryCode: settingsCountry,
    });
  }, [companyForm.ui_language, settingsCountry, t]);

  const timezoneOptions = useMemo(() => {
    const base = getTimezoneOptions();
    const current = companyForm.timezone;
    if (current && !base.some((tz) => tz.value === current)) {
      return [{ value: current, label: current.replace(/_/g, ' ') }, ...base];
    }
    return base;
  }, [companyForm.timezone]);

  const tabs: { id: SettingsTab; label: string; icon: typeof Settings; adminOnly?: boolean }[] = [
    { id: 'company', label: t('settings.company'), icon: Store, adminOnly: true },
    { id: 'pos_rules', label: t('settings.posRules'), icon: Sliders, adminOnly: true },
    { id: 'pos_sync', label: t('settings.posSync'), icon: Cloud, adminOnly: true },
    ...(isPakistan(settingsCountry)
      ? [{ id: 'fbr' as const, label: t('settings.fbr'), icon: FileCheck, adminOnly: true }]
      : []),
    ...(isSaudiArabia(settingsCountry)
      ? [{ id: 'zatca' as const, label: t('settings.zatca', { defaultValue: 'ZATCA' }), icon: FileCheck, adminOnly: true }]
      : []),
    ...(isUae(settingsCountry)
      ? [{ id: 'uae' as const, label: t('settings.uaeEInvoicing', { defaultValue: 'UAE E-Invoicing' }), icon: FileText, adminOnly: true }]
      : []),
    { id: 'marketplaces', label: t('settings.marketplaces', { defaultValue: 'Marketplaces' }), icon: ShoppingBag, adminOnly: true },
    { id: 'marketing', label: t('settings.marketing', { defaultValue: 'Marketing' }), icon: Megaphone, adminOnly: true },
    { id: 'receipt_designer', label: t('settings.receiptDesigner'), icon: FileText, adminOnly: true },
    { id: 'cash_pockets', label: t('settings.cashPockets'), icon: Landmark, adminOnly: true },
    { id: 'banks', label: t('settings.banks'), icon: CreditCard, adminOnly: true },
    { id: 'card_machines', label: 'Card machines', icon: CreditCard, adminOnly: true },
    { id: 'printer', label: t('settings.printer'), icon: Printer, adminOnly: true },
    { id: 'scale', label: t('settings.weighingScale', { defaultValue: 'Weighing Scale' }), icon: Scale, adminOnly: true },
    { id: 'starter_catalog', label: t('nav.starterCatalog'), icon: Package, adminOnly: true },
    ...(showBrandModel(companyForm.business_type || profile?.business_type)
      ? [
        { id: 'brands' as const, label: t('settings.brands'), icon: Tag, adminOnly: true },
        { id: 'models' as const, label: t('settings.models'), icon: Tag, adminOnly: true },
      ]
      : []),
    { id: 'data', label: t('settings.data'), icon: Database, adminOnly: true },
    { id: 'privacy', label: t('settings.gdpr'), icon: Shield, adminOnly: true },
  ];

  const visibleTabs = tabs.filter((tab) => !tab.adminOnly || isCompanyAdminUser);

  useEffect(() => {
    if (!visibleTabs.some((tab) => tab.id === activeTab)) {
      handleTabChange('company');
    }
  }, [activeTab, visibleTabs]);

  const status = trialStatus?.status || profile?.status || 'Trial Active';
  const daysRemaining = trialStatus?.days_remaining || 0;

  return (
    <div className="flex h-screen w-full max-w-[100%] overflow-hidden bg-gray-50">
      <Sidebar />
      <div className="flex-1 min-w-0 overflow-x-hidden overflow-y-auto">
        <TrialBanner />
        <div className="p-4 sm:p-6 min-w-0 w-full max-w-full box-border">
          <div className="mb-6 flex items-start sm:items-center gap-3 min-w-0">
            <button onClick={toggleSidebar} className="lg:hidden p-2 -ml-2 mt-0.5 sm:mt-0 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors shrink-0">
              <Menu className="w-5 h-5" />
            </button>
            <div className="min-w-0">
              <h1 className="text-2xl font-bold text-gray-900">{t('settings.title')}</h1>
              <p className="text-gray-500 text-sm">{t('settings.subtitle')}</p>
            </div>
          </div>

          <div className="flex flex-col lg:flex-row gap-6 min-w-0 w-full max-w-full">
            {/* Sidebar Tabs */}
            <div className="w-full lg:w-56 xl:w-64 shrink-0 min-w-0">
              {/* Mobile Dropdown */}
              <div className="block lg:hidden">
                <select
                  value={activeTab}
                  onChange={(e) => handleTabChange(e.target.value as SettingsTab)}
                  className="input-field bg-white shadow-sm"
                >
                  {visibleTabs.map((tab) => (
                    <option key={tab.id} value={tab.id}>{tab.label}</option>
                  ))}
                </select>
              </div>

              {/* Desktop Tabs */}
              <div className="hidden lg:flex card overflow-hidden flex-col">
                {visibleTabs.map((tab) => (
                  <button key={tab.id} onClick={() => handleTabChange(tab.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors text-left ${activeTab === tab.id ? 'bg-primary-50 text-primary-700 border-l-3 border-primary-600' : 'text-gray-600 hover:bg-gray-50'
                      }`}>
                    <tab.icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0 max-w-full">
              {/* Company Settings */}
              {activeTab === 'company' && (
                <div className="space-y-4">
                  <div className="card p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('settings.companyInfo')}</h2>
                    <form onSubmit={handleCompanySave} className="space-y-4 max-w-xl">
                      <div className="mb-6 flex items-center gap-6">
                        <div className="w-20 h-20 rounded-xl border border-gray-200 overflow-hidden bg-gray-50 flex items-center justify-center shrink-0">
                          {companyForm.company_logo ? (
                            <img src={resolveFileUrl(companyForm.company_logo)} alt="Logo" className="w-full h-full object-contain bg-white" />
                          ) : (
                            <Store className="w-8 h-8 text-gray-300" />
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.companyLogo')}</label>
                          <input
                            type="file"
                            accept="image/*"
                            className="text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                            onChange={async (e) => {
                              if (e.target.files && e.target.files[0]) {
                                const fd = new FormData();
                                fd.append('file', e.target.files[0]);
                                try {
                                  const res = await saasApi.uploadFile(fd);
                                  const fileUrl = res.data.message.file_url as string;
                                  setCompanyForm({ ...companyForm, company_logo: fileUrl });
                                  // Keep sidebar logo in sync immediately
                                  qc.setQueryData(['profile'], (prev: Record<string, unknown> | undefined) =>
                                    prev ? { ...prev, company_logo: fileUrl } : { company_logo: fileUrl },
                                  );
                                } catch (err) {
                                  console.error('Upload failed', err);
                                }
                              }
                            }}
                          />
                          <p className="text-xs text-gray-400 mt-2">{t('settings.logoHint')}</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.companyCode')}</label>
                          <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-500 font-mono font-bold select-all">
                            {profile?.company_code || company?.code || '-'}
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.domainName')}</label>
                          <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-500 font-mono select-all">
                            {profile?.subdomain
                              ? `${profile.subdomain.replace(/\.(saledesk\.(co\.uk|pk))$/i, '')}.${getAppBaseDomain()}`
                              : getAppBaseDomain()}
                          </div>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.companyName')}</label>
                        <input className="input-field" value={companyForm.company_name}
                          onChange={(e) => setCompanyForm({ ...companyForm, company_name: e.target.value })} />
                      </div>
                      {isGcc(companyForm.country) && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.companyNameAr', { defaultValue: 'Company Name (Arabic)' })}</label>
                          <input className="input-field" dir="rtl" value={companyForm.company_name_ar}
                            onChange={(e) => setCompanyForm({ ...companyForm, company_name_ar: e.target.value })} />
                        </div>
                      )}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.country')}</label>
                          <select
                            className="input-field bg-gray-50 text-gray-600 cursor-not-allowed"
                            value={companyForm.country}
                            disabled
                          >
                            {COUNTRIES.map((c) => (
                              <option key={c.code} value={c.code}>{t(`countries.${c.code}`, { defaultValue: c.name })}</option>
                            ))}
                          </select>
                          <p className="text-xs text-gray-500 mt-1">{t('settings.signupOnlyField')}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.currency')}</label>
                          <select
                            className="input-field bg-gray-50 text-gray-600 cursor-not-allowed"
                            value={companyForm.currency}
                            disabled
                          >
                            {Object.values(CURRENCIES).map((c) => (
                              <option key={c.code} value={c.code}>
                                {c.code} ({c.symbol}) — {t(`currencies.${c.code}`, { defaultValue: c.name })}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.ownerName')}</label>
                          <input className="input-field" value={companyForm.owner_name}
                            onChange={(e) => setCompanyForm({ ...companyForm, owner_name: e.target.value })} />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.businessType')}</label>
                          <select className="input-field bg-gray-50 text-gray-600 cursor-not-allowed" value={companyForm.business_type} disabled>
                            {BUSINESS_TYPES.map((bt) => (
                              <option key={bt.value} value={bt.value}>{t(bt.labelKey)}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.defaultUiLanguage')}</label>
                          <select
                            className="input-field"
                            value={companyForm.ui_language}
                            onChange={(e) => {
                              const lang = e.target.value;
                              setCompanyForm({ ...companyForm, ui_language: lang });
                              void setAppLanguage(user?.ui_language, lang).then((resolvedLang) => {
                                syncLocaleContext(companyForm.country, companyForm.currency, resolvedLang);
                              });
                            }}
                          >
                            {companyLanguageOptions.map((l) => (
                              <option key={l.code} value={l.code}>{l.label}</option>
                            ))}
                          </select>
                          <p className="text-xs text-gray-500 mt-1">{t('settings.defaultUiLanguageHelp')}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.timezone')}</label>
                          <select
                            className="input-field"
                            value={companyForm.timezone}
                            onChange={(e) => setCompanyForm({ ...companyForm, timezone: e.target.value })}
                          >
                            {timezoneOptions.map((tz) => (
                              <option key={tz.value} value={tz.value}>{tz.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {getCountry(companyForm.country).taxIdLabel}
                            {getCountry(companyForm.country).taxIdLabelSecondary
                              ? ` / ${getCountry(companyForm.country).taxIdLabelSecondary}`
                              : ''}
                          </label>
                          <input
                            className="input-field"
                            value={isPakistan(companyForm.country) ? '' : companyForm.tax_registration_id}
                            placeholder={getCountry(companyForm.country).taxIdLabel}
                            onChange={(e) => setCompanyForm({ ...companyForm, tax_registration_id: e.target.value })}
                            disabled={isPakistan(companyForm.country)}
                          />
                          {isPakistan(companyForm.country) && (
                            <p className="text-xs text-gray-500 mt-1">{t('settings.fbrTaxHint')}</p>
                          )}
                          {isSaudiArabia(companyForm.country) && (
                            <p className="text-xs text-gray-500 mt-1">{t('settings.zatcaHint')}</p>
                          )}
                          {isUae(companyForm.country) && (
                            <p className="text-xs text-gray-500 mt-1">{t('settings.uaeHint')}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center justify-between py-2">
                        <div className="min-w-0 pr-3">
                          <p className="font-medium text-gray-900 text-sm">
                            {isUk(companyForm.country)
                              ? 'Taxes are included in price'
                              : t('settings.pricesIncludeTax')}
                          </p>
                          <p className="text-xs text-gray-500">
                            {isUk(companyForm.country)
                              ? companyForm.prices_include_tax
                                ? 'Sale prices include VAT. Turn off to add VAT on top (Nett + VAT), matching UK trade invoices.'
                                : 'VAT is added on top of the sale price (Nett + VAT). Set each product’s VAT category (Z / R / S) on Items.'
                              : getDefaultVatRate(companyForm.country) > 0
                                ? t('settings.pricesIncludeTaxHelpWithRate', {
                                    rate: getDefaultVatRate(companyForm.country),
                                  })
                                : isGcc(companyForm.country)
                                  ? t('settings.pricesIncludeTaxHelpGcc')
                                  : t('settings.pricesIncludeTaxHelp')}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() =>
                            setCompanyForm({
                              ...companyForm,
                              prices_include_tax: !companyForm.prices_include_tax,
                            })
                          }
                          className={`px-4 py-1.5 text-xs font-semibold rounded-full border transition-all select-none shrink-0 ${
                            companyForm.prices_include_tax
                              ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                              : 'bg-gray-50 text-gray-500 border-gray-200'
                          }`}
                          title={
                            companyForm.prices_include_tax
                              ? 'Taxes included in price'
                              : 'Taxes not included — VAT on top'
                          }
                        >
                          {isUk(companyForm.country)
                            ? companyForm.prices_include_tax
                              ? 'Yes'
                              : 'No'
                            : companyForm.prices_include_tax
                              ? t('common.inclusive')
                              : t('common.exclusive')}
                        </button>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.privacyUrl')}</label>
                        <input
                          className="input-field"
                          value={companyForm.privacy_policy_url}
                          placeholder="https://…"
                          onChange={(e) => setCompanyForm({ ...companyForm, privacy_policy_url: e.target.value })}
                        />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.email')}</label>
                          <input type="email" className="input-field bg-gray-50 text-gray-500 cursor-not-allowed" value={companyForm.email} readOnly
                            onChange={(e) => setCompanyForm({ ...companyForm, email: e.target.value })} />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.mobile')}</label>
                          <input
                            type="tel"
                            inputMode="numeric"
                            maxLength={isPakistan(companyForm.country) ? 11 : 15}
                            className={`input-field ${companyMobileError ? 'border-red-400 focus:ring-red-300' : ''}`}
                            value={companyForm.mobile}
                            placeholder={isPakistan(companyForm.country) ? '03XXXXXXXXX' : 'Phone number'}
                            onChange={(e) => {
                              setCompanyMobileError('');
                              setCompanyForm({
                                ...companyForm,
                                mobile: sanitizePhoneInput(e.target.value, companyForm.country),
                              });
                            }}
                          />
                          {companyMobileError && (
                            <p className="text-xs text-red-500 mt-1">{companyMobileError}</p>
                          )}
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.region')}</label>
                          <select
                            className="input-field"
                            value={companyForm.province}
                            onChange={(e) => setCompanyForm({ ...companyForm, province: e.target.value })}
                          >
                            <option value="">{t('common.select')}</option>
                            {getRegionsForCountry(companyForm.country).map((r) => (
                              <option key={r} value={r}>{r}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {companyForm.country === 'US' ? t('common.zipCode') : t('common.postalCode')}
                          </label>
                          {getCountry(companyForm.country).usePostcodeLookup ? (
                            <UkPostcodeLookup
                              postalCode={companyForm.postal_code}
                              onPostalCodeChange={(v) => setCompanyForm((prev) => ({ ...prev, postal_code: v }))}
                              onFill={(fields) => {
                                setCompanyForm((prev) => ({
                                  ...prev,
                                  postal_code: fields.postal_code,
                                  province: fields.province || prev.province,
                                  city: fields.city || prev.city,
                                  address: fields.address !== undefined ? fields.address : prev.address,
                                }));
                              }}
                            />
                          ) : (
                            <input
                              className="input-field"
                              value={companyForm.postal_code}
                              onChange={(e) => setCompanyForm({ ...companyForm, postal_code: e.target.value })}
                            />
                          )}
                        </div>
                      </div>
                      {(companyForm.country === 'GB' || companyForm.country === 'UK') && (
                        <div className="rounded-xl border border-gray-200 bg-gray-50/80 p-4 space-y-3">
                          <div>
                            <p className="text-sm font-semibold text-gray-900">{t('ukAddress.idealTitle')}</p>
                            <p className="text-xs text-gray-500 mt-0.5">{t('ukAddress.idealHelp')}</p>
                          </div>
                          <label className="flex items-center gap-2 text-sm cursor-pointer">
                            <input
                              type="checkbox"
                              className="rounded accent-primary-600"
                              checked={companyForm.enable_ideal_postcodes}
                              onChange={(e) => setCompanyForm({ ...companyForm, enable_ideal_postcodes: e.target.checked })}
                            />
                            {t('ukAddress.enableIdeal')}
                          </label>
                          {companyForm.enable_ideal_postcodes && (
                            <>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  {t('ukAddress.apiKey')}
                                  {profile?.has_ideal_postcodes_key && (
                                    <span className="ml-2 text-xs text-emerald-600 font-normal">{t('ukAddress.keySaved')}</span>
                                  )}
                                </label>
                                <input
                                  type="password"
                                  className="input-field w-full"
                                  autoComplete="new-password"
                                  placeholder={profile?.has_ideal_postcodes_key ? '••••••••' : t('ukAddress.apiKeyPlaceholder')}
                                  value={companyForm.ideal_postcodes_api_key}
                                  onChange={(e) => {
                                    idealKeyDraftRef.current = e.target.value;
                                    setCompanyForm({ ...companyForm, ideal_postcodes_api_key: e.target.value });
                                  }}
                                />
                                <p className="text-xs text-gray-500 mt-1">{t('ukAddress.keyNotShownAgain')}</p>
                              </div>
                              <div className="flex flex-wrap gap-2">
                                <button
                                  type="button"
                                  className="btn-primary text-sm inline-flex items-center gap-2"
                                  disabled={
                                    updateCompanyMutation.isPending
                                    || (!companyForm.ideal_postcodes_api_key.trim() && !profile?.has_ideal_postcodes_key)
                                  }
                                  onClick={() => {
                                    updateCompanyMutation.mutate({
                                      enable_ideal_postcodes: companyForm.enable_ideal_postcodes ? 1 : 0,
                                      ...(companyForm.ideal_postcodes_api_key.trim()
                                        ? { ideal_postcodes_api_key: companyForm.ideal_postcodes_api_key.trim() }
                                        : {}),
                                    });
                                  }}
                                >
                                  {updateCompanyMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                  {t('ukAddress.saveKey')}
                                </button>
                                <button
                                  type="button"
                                  className="btn-secondary text-sm inline-flex items-center gap-2"
                                  disabled={idealTesting}
                                  onClick={async () => {
                                    setIdealTesting(true);
                                    setIdealTestMsg('');
                                    try {
                                      const draft = companyForm.ideal_postcodes_api_key.trim();
                                      // Persist draft key before testing so Find address uses it
                                      if (draft) {
                                        await authApi.updateCompanyProfile({
                                          enable_ideal_postcodes: 1,
                                          ideal_postcodes_api_key: draft,
                                        });
                                        idealKeyDraftRef.current = '';
                                        setCompanyForm((prev) => ({ ...prev, ideal_postcodes_api_key: '' }));
                                        await qc.invalidateQueries({ queryKey: ['profile'] });
                                      }
                                      const r = await testIdealPostcodesKey(draft || undefined, 'PR1 3BU');
                                      setIdealTestMsg(
                                        t('ukAddress.testOk', {
                                          count: r.address_count ?? 0,
                                          postcode: r.postcode || 'PR1 3BU',
                                        }),
                                      );
                                    } catch (err) {
                                      setIdealTestMsg((err as Error).message || t('ukAddress.testFailed'));
                                    } finally {
                                      setIdealTesting(false);
                                    }
                                  }}
                                >
                                  {idealTesting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                                  {t('ukAddress.testLookup')}
                                </button>
                              </div>
                              {idealTestMsg && (
                                <p className="text-xs text-gray-600">{idealTestMsg}</p>
                              )}
                            </>
                          )}
                        </div>
                      )}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.city')}</label>
                          {getCountry(companyForm.country).useCityMaster ? (
                            <select className="input-field" value={companyForm.city}
                              onChange={(e) => setCompanyForm({ ...companyForm, city: e.target.value })}>
                              <option value="">{t('common.select')}</option>
                              {cities.map((c) => (
                                <option key={c.name} value={c.name}>{c.city}</option>
                              ))}
                            </select>
                          ) : (
                            <input
                              className="input-field"
                              value={companyForm.city}
                              onChange={(e) => setCompanyForm({ ...companyForm, city: e.target.value })}
                            />
                          )}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.address')}</label>
                        <textarea rows={3} className="input-field resize-none" value={companyForm.address}
                          onChange={(e) => setCompanyForm({ ...companyForm, address: e.target.value })} />
                      </div>
                      <button type="submit" disabled={updateCompanyMutation.isPending}
                        className="btn-primary flex items-center gap-2">
                        {updateCompanyMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        {t('common.save')}
                      </button>
                    </form>
                  </div>
                </div>
              )}

              {/* POS Rules */}
              {activeTab === 'pos_rules' && (() => {
                const query = posRulesSearch.trim().toLowerCase();

                const categoryChips = [
                  { id: 'all', label: 'All Settings' },
                  { id: 'delivery', label: 'Delivery' },
                  { id: 'pricing', label: 'Taxes & Pricing' },
                  { id: 'inventory', label: 'Products & Stock' },
                  { id: 'shifts', label: 'Shifts & Cashiers' },
                  { id: 'layout', label: 'Screen & Display' },
                  { id: 'hardware', label: 'Hardware & Reports' },
                ];

                const sections = [
                  {
                    id: 'delivery',
                    title: 'Delivery & Fulfillment',
                    subtitle: 'Manage delivery enablement, fulfillment choices, and pricing modes',
                    icon: Truck,
                    color: 'text-blue-600 bg-blue-50 border-blue-200',
                    items: [
                      {
                        id: 'enable_delivery',
                        title: 'Enable Delivery',
                        description: 'Enable pickup & delivery fulfillment choices in POS, deliveries tab, and delivery pricing.',
                        keywords: ['delivery', 'deliveries', 'fulfillment', 'shipping', 'order', 'courier', 'rider', 'pickup', 'runtime_charge', 'pricing', 'product_price'],
                        render: () => (
                          <div key="enable_delivery" className="py-4 space-y-3">
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="flex items-center gap-2">
                                  <Truck className="w-4 h-4 text-blue-600" />
                                  <p className="font-medium text-gray-900 text-sm">Enable Delivery</p>
                                </div>
                                <p className="text-xs text-gray-500 mt-0.5">
                                  Enable pickup &amp; delivery fulfillment choices in POS, deliveries tab, and delivery pricing.
                                </p>
                              </div>
                              <button
                                type="button"
                                onClick={() => setPosForm({ ...posForm, enable_delivery: !posForm.enable_delivery })}
                                className={`px-4 py-1.5 text-xs font-semibold rounded-full border transition-all select-none ${posForm.enable_delivery
                                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
                                  : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'
                                  }`}
                              >
                                {posForm.enable_delivery ? t('common.enabled') : t('common.disabled')}
                              </button>
                            </div>

                            {posForm.enable_delivery && (
                              <div className="mt-3 p-3.5 bg-blue-50/50 rounded-xl border border-blue-100 space-y-2.5 animate-in fade-in duration-150">
                                <label className="block text-xs font-semibold text-gray-700">
                                  Delivery Pricing Method
                                </label>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                                  <div
                                    onClick={() => setPosForm({ ...posForm, delivery_pricing_mode: 'product_price' })}
                                    className={`cursor-pointer p-3 rounded-lg border text-xs transition-all ${(posForm.delivery_pricing_mode || 'product_price') === 'product_price'
                                      ? 'bg-white border-blue-600 shadow-sm ring-1 ring-blue-600'
                                      : 'bg-white/80 border-gray-200 hover:border-gray-300'
                                      }`}
                                  >
                                    <div className="flex items-center justify-between font-semibold text-gray-900 mb-1">
                                      <span>Pre-set in Products</span>
                                      <input
                                        type="radio"
                                        name="delivery_pricing_mode"
                                        checked={(posForm.delivery_pricing_mode || 'product_price') === 'product_price'}
                                        onChange={() => setPosForm({ ...posForm, delivery_pricing_mode: 'product_price' })}
                                        className="text-blue-600"
                                      />
                                    </div>
                                    <p className="text-gray-500 text-[11px] leading-relaxed">
                                      Set delivery price per item/variant in Products catalog. POS uses product delivery prices on delivery orders.
                                    </p>
                                  </div>

                                  <div
                                    onClick={() => setPosForm({ ...posForm, delivery_pricing_mode: 'runtime_charge' })}
                                    className={`cursor-pointer p-3 rounded-lg border text-xs transition-all ${posForm.delivery_pricing_mode === 'runtime_charge'
                                      ? 'bg-white border-blue-600 shadow-sm ring-1 ring-blue-600'
                                      : 'bg-white/80 border-gray-200 hover:border-gray-300'
                                      }`}
                                  >
                                    <div className="flex items-center justify-between font-semibold text-gray-900 mb-1">
                                      <span>Decide on Delivery Order</span>
                                      <input
                                        type="radio"
                                        name="delivery_pricing_mode"
                                        checked={posForm.delivery_pricing_mode === 'runtime_charge'}
                                        onChange={() => setPosForm({ ...posForm, delivery_pricing_mode: 'runtime_charge' })}
                                        className="text-blue-600"
                                      />
                                    </div>
                                    <p className="text-gray-500 text-[11px] leading-relaxed">
                                      Prompt for delivery price/charge when creating each delivery order in POS. Added as an overall order delivery fee.
                                    </p>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        ),
                      },
                    ],
                  },
                  {
                    id: 'pricing',
                    title: 'Pricing, Taxes & Discounts',
                    subtitle: 'Manage tax calculation, cashier discounts, invoice print settings, and write-off limits',
                    icon: Tag,
                    color: 'text-emerald-600 bg-emerald-50 border-emerald-200',
                    items: [
                      {
                        id: 'enable_item_tax',
                        title: t('settings.enableItemTax'),
                        description: t('settings.enableItemTaxHelp'),
                        keywords: ['tax', 'taxes', 'vat', 'gst', 'item tax', 'sales tax', 'auto tax'],
                        render: () => (
                          <div key="enable_item_tax" className="flex items-center justify-between py-3">
                            <div>
                              <p className="font-medium text-gray-900 text-sm">{t('settings.enableItemTax')}</p>
                              <p className="text-xs text-gray-500">{t('settings.enableItemTaxHelp')}</p>
                            </div>
                            <button
                              type="button"
                              onClick={() => setPosForm({ ...posForm, enable_item_tax: !posForm.enable_item_tax })}
                              className={`px-4 py-1.5 text-xs font-semibold rounded-full border transition-all select-none ${posForm.enable_item_tax
                                ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
                                : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'
                                }`}
                            >
                              {posForm.enable_item_tax ? t('common.enabled') : t('common.disabled')}
                            </button>
                          </div>
                        ),
                      },
                      {
                        id: 'tax_label',
                        title: t('settings.taxFieldLabel'),
                        description: t('settings.taxFieldLabelHelp'),
                        keywords: ['tax label', 'vat label', 'gst label', 'name', 'field label'],
                        render: () => (
                          <div key="tax_label" className="flex items-center justify-between py-3 gap-4">
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-gray-900 text-sm">{t('settings.taxFieldLabel')}</p>
                              <p className="text-xs text-gray-500">{t('settings.taxFieldLabelHelp')}</p>
                            </div>
                            <input
                              type="text"
                              maxLength={40}
                              className="w-36 input-field text-sm"
                              value={posForm.tax_label || 'Tax'}
                              onChange={(e) => setPosForm({ ...posForm, tax_label: e.target.value })}
                              placeholder="Tax"
                            />
                          </div>
                        ),
                      },
                      {
                        id: 'show_tax_field_in_pos',
                        title: t('settings.showTaxInPos'),
                        description: posForm.enable_item_tax ? t('settings.showTaxInPosAuto') : t('settings.showTaxInPosManual'),
                        keywords: ['tax in pos', 'show tax', 'cart tax', 'manual tax'],
                        render: () => (
                          <div key="show_tax_field_in_pos" className="flex items-center justify-between py-3">
                            <div>
                              <p className="font-medium text-gray-900 text-sm">{t('settings.showTaxInPos')}</p>
                              <p className="text-xs text-gray-500">
                                {posForm.enable_item_tax
                                  ? t('settings.showTaxInPosAuto')
                                  : t('settings.showTaxInPosManual')}
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={() => setPosForm({ ...posForm, show_tax_field_in_pos: !posForm.show_tax_field_in_pos })}
                              disabled={!!posForm.enable_item_tax}
                              className={`px-4 py-1.5 text-xs font-semibold rounded-full border transition-all select-none disabled:opacity-50 ${posForm.show_tax_field_in_pos || posForm.enable_item_tax
                                ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
                                : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'
                                }`}
                            >
                              {posForm.show_tax_field_in_pos || posForm.enable_item_tax ? t('common.enabled') : t('common.disabled')}
                            </button>
                          </div>
                        ),
                      },
                      {
                        id: 'show_tax_in_invoice',
                        title: t('settings.showTaxInInvoice'),
                        description: t('settings.showTaxInInvoiceHelp'),
                        keywords: ['tax invoice', 'tax print', 'receipt tax', 'tax breakdown'],
                        render: () => (
                          <div key="show_tax_in_invoice" className="flex items-center justify-between py-3">
                            <div>
                              <p className="font-medium text-gray-900 text-sm">{t('settings.showTaxInInvoice')}</p>
                              <p className="text-xs text-gray-500">{t('settings.showTaxInInvoiceHelp')}</p>
                            </div>
                            <button
                              type="button"
                              onClick={() => setPosForm({ ...posForm, show_tax_in_invoice: !posForm.show_tax_in_invoice })}
                              className={`px-4 py-1.5 text-xs font-semibold rounded-full border transition-all select-none ${posForm.show_tax_in_invoice
                                ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
                                : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'
                                }`}
                            >
                              {posForm.show_tax_in_invoice ? t('common.enabled') : t('common.disabled')}
                            </button>
                          </div>
                        ),
                      },
                      {
                        id: 'discount_settings',
                        title: t('settings.showDiscountInPos'),
                        description: `${t('settings.showDiscountInPosHelp')} ${t('settings.maxDiscountLimit')}`,
                        keywords: ['discount', 'discounts', 'max discount', 'cashier discount', 'percentage', 'discount limit'],
                        render: () => (
                          <div key="discount_settings" className="flex items-center justify-between py-3 gap-4">
                            <div>
                              <p className="font-medium text-gray-900 text-sm">{t('settings.showDiscountInPos')}</p>
                              <p className="text-xs text-gray-500">{t('settings.showDiscountInPosHelp')}</p>
                            </div>
                            <div className="flex items-center gap-3">
                              {posForm.show_discount_field_in_pos && (
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-gray-500">{t('settings.maxDiscountLimit')}</span>
                                  <div className="relative w-24">
                                    <input
                                      type="number"
                                      min={0}
                                      max={100}
                                      step={0.01}
                                      value={posForm.max_discount || ''}
                                      onChange={(e) => setPosForm({ ...posForm, max_discount: parseFloat(e.target.value) || 0 })}
                                      className="w-full text-xs border border-gray-200 rounded pl-2 pr-6 py-1.5 focus:ring-1 focus:ring-primary-500 outline-none text-right font-medium"
                                      placeholder="0"
                                    />
                                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs font-medium">%</span>
                                  </div>
                                </div>
                              )}
                              <button
                                type="button"
                                onClick={() => setPosForm({ ...posForm, show_discount_field_in_pos: !posForm.show_discount_field_in_pos })}
                                className={`px-4 py-1.5 text-xs font-semibold rounded-full border transition-all select-none ${posForm.show_discount_field_in_pos
                                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
                                  : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'
                                  }`}
                              >
                                {posForm.show_discount_field_in_pos ? t('common.enabled') : t('common.disabled')}
                              </button>
                            </div>
                          </div>
                        ),
                      },
                      {
                        id: 'show_discount_in_invoice_print',
                        title: t('settings.showDiscountInPrint'),
                        description: t('settings.showDiscountInPrintHelp'),
                        keywords: ['discount print', 'receipt discount', 'invoice discount'],
                        render: () => (
                          <div key="show_discount_in_invoice_print" className="flex items-center justify-between py-3">
                            <div>
                              <p className="font-medium text-gray-900 text-sm">{t('settings.showDiscountInPrint')}</p>
                              <p className="text-xs text-gray-500">{t('settings.showDiscountInPrintHelp')}</p>
                            </div>
                            <button
                              type="button"
                              onClick={() => setPosForm({ ...posForm, show_discount_in_invoice_print: !posForm.show_discount_in_invoice_print })}
                              className={`px-4 py-1.5 text-xs font-semibold rounded-full border transition-all select-none ${posForm.show_discount_in_invoice_print
                                ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
                                : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'
                                }`}
                            >
                              {posForm.show_discount_in_invoice_print ? t('common.enabled') : t('common.disabled')}
                            </button>
                          </div>
                        ),
                      },
                      {
                        id: 'max_round_off',
                        title: t('settings.maxWriteOff'),
                        description: t('settings.maxWriteOffHelp', { symbol: getCurrencySymbol() }),
                        keywords: ['round off', 'write off', 'change', 'cents', 'penny', 'rounding', 'limit'],
                        render: () => (
                          <div key="max_round_off" className="flex items-center justify-between py-3">
                            <div>
                              <p className="font-medium text-gray-900 text-sm">{t('settings.maxWriteOff')}</p>
                              <p className="text-xs text-gray-500 mt-0.5">{t('settings.maxWriteOffHelp', { symbol: getCurrencySymbol() })}</p>
                            </div>
                            <div className="relative w-32 ml-4">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">{getCurrencySymbol()}</span>
                              <input
                                type="number"
                                min="0"
                                className="input-field pl-8"
                                value={posForm.max_round_off}
                                onChange={(e) => setPosForm({ ...posForm, max_round_off: parseFloat(e.target.value) || 0 })}
                              />
                            </div>
                          </div>
                        ),
                      },
                    ],
                  },
                  {
                    id: 'inventory',
                    title: 'Products & Inventory Controls',
                    subtitle: 'Control out-of-stock sales, service item visibility, and barcode tags',
                    icon: Package,
                    color: 'text-purple-600 bg-purple-50 border-purple-200',
                    items: [
                      {
                        id: 'allow_sell_out_of_stock_item',
                        title: t('settings.allowSellOos'),
                        description: t('settings.allowSellOosHelp'),
                        keywords: ['stock', 'out of stock', 'oos', 'inventory', 'allow negative', 'negative stock'],
                        render: () => (
                          <div key="allow_sell_out_of_stock_item" className="flex items-center justify-between py-3">
                            <div>
                              <p className="font-medium text-gray-900 text-sm">{t('settings.allowSellOos')}</p>
                              <p className="text-xs text-gray-500">{t('settings.allowSellOosHelp')}</p>
                            </div>
                            <button
                              type="button"
                              onClick={() => setPosForm({ ...posForm, allow_sell_out_of_stock_item: !posForm.allow_sell_out_of_stock_item })}
                              className={`px-4 py-1.5 text-xs font-semibold rounded-full border transition-all select-none ${posForm.allow_sell_out_of_stock_item
                                ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
                                : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'
                                }`}
                            >
                              {posForm.allow_sell_out_of_stock_item ? t('common.enabled') : t('common.disabled')}
                            </button>
                          </div>
                        ),
                      },
                      {
                        id: 'sell_only_products',
                        title: t('settings.sellOnlyProducts'),
                        description: t('settings.sellOnlyProductsHelp'),
                        keywords: ['products', 'services', 'hide services', 'sell only products', 'catalog'],
                        render: () => (
                          <div key="sell_only_products" className="flex items-center justify-between py-3">
                            <div>
                              <p className="font-medium text-gray-900 text-sm">{t('settings.sellOnlyProducts')}</p>
                              <p className="text-xs text-gray-500">{t('settings.sellOnlyProductsHelp')}</p>
                            </div>
                            <button
                              type="button"
                              onClick={() => setPosForm({ ...posForm, sell_only_products: !posForm.sell_only_products })}
                              className={`px-4 py-1.5 text-xs font-semibold rounded-full border transition-all select-none ${posForm.sell_only_products
                                ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
                                : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'
                                }`}
                            >
                              {posForm.sell_only_products ? t('common.enabled') : t('common.disabled')}
                            </button>
                          </div>
                        ),
                      },
                      {
                        id: 'use_barcode_or_qr',
                        title: t('settings.tagCodeType'),
                        description: t('settings.tagCodeTypeHelp'),
                        keywords: ['barcode', 'qr code', 'scan', 'scanner', 'code type', 'tag'],
                        render: () => (
                          <div key="use_barcode_or_qr" className="flex items-center justify-between py-3">
                            <div>
                              <p className="font-medium text-gray-900 text-sm">{t('settings.tagCodeType')}</p>
                              <p className="text-xs text-gray-500">{t('settings.tagCodeTypeHelp')}</p>
                            </div>
                            <select
                              value={posForm.use_barcode_or_qr}
                              onChange={(e) => setPosForm({ ...posForm, use_barcode_or_qr: e.target.value as 'Barcode' | 'QR Code' })}
                              className="input-field w-36 ml-4"
                            >
                              <option value="Barcode">{t('settings.barcode')}</option>
                              <option value="QR Code">{t('settings.qrCode')}</option>
                            </select>
                          </div>
                        ),
                      },
                      {
                        id: 'print_price_on_label',
                        title: t('settings.printPriceOnTag'),
                        description: t('settings.printPriceOnTagHelp'),
                        keywords: ['price on tag', 'label price', 'print price', 'tag price', 'barcode label'],
                        render: () => (
                          <div key="print_price_on_label" className="flex items-center justify-between py-3">
                            <div>
                              <p className="font-medium text-gray-900 text-sm">{t('settings.printPriceOnTag')}</p>
                              <p className="text-xs text-gray-500">{t('settings.printPriceOnTagHelp')}</p>
                            </div>
                            <button
                              type="button"
                              onClick={() => setPosForm({ ...posForm, print_price_on_label: !posForm.print_price_on_label })}
                              className={`px-4 py-1.5 text-xs font-semibold rounded-full border transition-all select-none ${posForm.print_price_on_label
                                ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
                                : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'
                                }`}
                            >
                              {posForm.print_price_on_label ? t('common.enabled') : t('common.disabled')}
                            </button>
                          </div>
                        ),
                      },
                    ],
                  },
                  {
                    id: 'shifts',
                    title: 'Shift & Cashier Management',
                    subtitle: 'Shift time limits, sales commission tracking, and cashier view permissions',
                    icon: Clock,
                    color: 'text-amber-600 bg-amber-50 border-amber-200',
                    items: [
                      {
                        id: 'shift_time_limit',
                        title: 'Enforce shift time limit',
                        description: 'Cashiers must close and reopen shift after the configured hours.',
                        keywords: ['shift', 'shift limit', 'hours', 'expiry', 'warning', 'close shift', 'time limit', 'enforce shift'],
                        render: () => (
                          <div key="shift_time_limit" className="py-3 space-y-3">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-medium text-gray-900 text-sm">Enforce shift time limit</p>
                                <p className="text-xs text-gray-500">Cashiers must close and reopen shift after the configured hours.</p>
                              </div>
                              <button
                                type="button"
                                onClick={() => setPosForm({ ...posForm, enforce_shift_time_limit: !posForm.enforce_shift_time_limit })}
                                className={`px-4 py-1.5 text-xs font-semibold rounded-full border transition-all select-none ${posForm.enforce_shift_time_limit
                                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
                                  : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'
                                  }`}
                              >
                                {posForm.enforce_shift_time_limit ? t('common.enabled') : t('common.disabled')}
                              </button>
                            </div>
                            {posForm.enforce_shift_time_limit && (
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-3 bg-amber-50/50 rounded-xl border border-amber-100">
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">Max shift hours</label>
                                  <input
                                    type="number"
                                    min={1}
                                    max={48}
                                    className="input-field text-sm"
                                    value={posForm.max_shift_hours ?? 24}
                                    onChange={(e) => setPosForm({ ...posForm, max_shift_hours: parseInt(e.target.value, 10) || 24 })}
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">Warning before expiry (hours)</label>
                                  <input
                                    type="number"
                                    min={1}
                                    className="input-field text-sm"
                                    value={posForm.shift_expiry_warning_hours ?? 22}
                                    onChange={(e) => setPosForm({ ...posForm, shift_expiry_warning_hours: parseInt(e.target.value, 10) || 22 })}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        ),
                      },
                      {
                        id: 'enable_commission',
                        title: t('settings.enableCommission'),
                        description: t('settings.enableCommissionHelp'),
                        keywords: ['commission', 'staff commission', 'sales commission', 'incentive', 'bonus'],
                        render: () => (
                          <div key="enable_commission" className="flex items-center justify-between py-3">
                            <div>
                              <p className="font-medium text-gray-900 text-sm">{t('settings.enableCommission')}</p>
                              <p className="text-xs text-gray-500">{t('settings.enableCommissionHelp')}</p>
                            </div>
                            <button
                              type="button"
                              onClick={() => setPosForm({ ...posForm, enable_commission: !posForm.enable_commission })}
                              className={`px-4 py-1.5 text-xs font-semibold rounded-full border transition-all select-none ${posForm.enable_commission
                                ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
                                : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'
                                }`}
                            >
                              {posForm.enable_commission ? t('common.enabled') : t('common.disabled')}
                            </button>
                          </div>
                        ),
                      },
                      {
                        id: 'show_today_invoices_to_cashier',
                        title: t('settings.showTodayInvoices'),
                        description: t('settings.showTodayInvoicesHelp'),
                        keywords: ['today invoices', 'cashier invoices', 'past sales', 'cashier view', 'history'],
                        render: () => (
                          <div key="show_today_invoices_to_cashier" className="flex items-center justify-between py-3">
                            <div>
                              <p className="font-medium text-gray-900 text-sm">{t('settings.showTodayInvoices')}</p>
                              <p className="text-xs text-gray-500">{t('settings.showTodayInvoicesHelp')}</p>
                            </div>
                            <button
                              type="button"
                              onClick={() => setPosForm({ ...posForm, show_today_invoices_to_cashier: !posForm.show_today_invoices_to_cashier })}
                              className={`px-4 py-1.5 text-xs font-semibold rounded-full border transition-all select-none ${posForm.show_today_invoices_to_cashier
                                ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
                                : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'
                                }`}
                            >
                              {posForm.show_today_invoices_to_cashier ? t('common.enabled') : t('common.disabled')}
                            </button>
                          </div>
                        ),
                      },
                    ],
                  },
                  {
                    id: 'layout',
                    title: 'Screen Layout & Display',
                    subtitle: 'Category bar positioning and secondary customer-facing display',
                    icon: Sliders,
                    color: 'text-indigo-600 bg-indigo-50 border-indigo-200',
                    items: [
                      {
                        id: 'pos_category_position',
                        title: t('settings.categoryBarPosition'),
                        description: t('settings.categoryBarPositionHelp'),
                        keywords: ['category', 'category bar', 'category position', 'layout', 'top', 'bottom', 'left', 'right'],
                        render: () => (
                          <div key="pos_category_position" className="flex items-center justify-between py-3">
                            <div>
                              <p className="font-medium text-gray-900 text-sm">{t('settings.categoryBarPosition')}</p>
                              <p className="text-xs text-gray-500">{t('settings.categoryBarPositionHelp')}</p>
                            </div>
                            <select
                              value={posForm.pos_category_position || 'Top'}
                              onChange={(e) =>
                                setPosForm({
                                  ...posForm,
                                  pos_category_position: e.target.value as PosSettings['pos_category_position'],
                                })
                              }
                              className="input-field w-44 ml-4"
                            >
                              <option value="Top">{t('settings.categoryPositionTop')}</option>
                              <option value="Bottom">{t('settings.categoryPositionBottom')}</option>
                              <option value="Left">{t('settings.categoryPositionLeft')}</option>
                              <option value="Right">{t('settings.categoryPositionRight')}</option>
                            </select>
                          </div>
                        ),
                      },
                      {
                        id: 'customer_display_enabled',
                        title: t('settings.customerDisplayEnabled', 'Customer Display Screen'),
                        description: t('settings.customerDisplayEnabledHelp', 'Show the customer-facing screen button on POS toolbar for dual-screen setups.'),
                        keywords: ['customer display', 'dual screen', 'second monitor', 'second screen', 'screen'],
                        render: () => (
                          <div key="customer_display_enabled" className="flex items-center justify-between py-3">
                            <div>
                              <p className="font-medium text-gray-900 text-sm">{t('settings.customerDisplayEnabled', 'Customer Display Screen')}</p>
                              <p className="text-xs text-gray-500">{t('settings.customerDisplayEnabledHelp', 'Show the customer-facing screen button on POS toolbar for dual-screen setups.')}</p>
                            </div>
                            <button
                              type="button"
                              onClick={() => setPosForm({ ...posForm, customer_display_enabled: !posForm.customer_display_enabled })}
                              className={`px-4 py-1.5 text-xs font-semibold rounded-full border transition-all select-none ${posForm.customer_display_enabled
                                ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
                                : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'
                                }`}
                            >
                              {posForm.customer_display_enabled ? t('common.enabled') : t('common.disabled')}
                            </button>
                          </div>
                        ),
                      },
                    ],
                  },
                  {
                    id: 'hardware',
                    title: 'Cash Drawer & Automated Reports',
                    subtitle: 'Drawer opening rules and daily sales summary automated email',
                    icon: Landmark,
                    color: 'text-slate-600 bg-slate-100 border-slate-200',
                    items: [
                      {
                        id: 'open_drawer_on_cash_payment',
                        title: t('settings.openDrawerOnCashPayment'),
                        description: t('settings.openDrawerOnCashPaymentHelp'),
                        keywords: ['drawer', 'cash drawer', 'cash payment', 'payment drawer', 'hardware'],
                        render: () => (
                          <div key="open_drawer_on_cash_payment" className="flex items-center justify-between py-3">
                            <div>
                              <p className="font-medium text-gray-900 text-sm">{t('settings.openDrawerOnCashPayment')}</p>
                              <p className="text-xs text-gray-500">{t('settings.openDrawerOnCashPaymentHelp')}</p>
                            </div>
                            <button
                              type="button"
                              onClick={() => setPosForm({ ...posForm, open_drawer_on_cash_payment: !posForm.open_drawer_on_cash_payment })}
                              className={`px-4 py-1.5 text-xs font-semibold rounded-full border transition-all select-none ${posForm.open_drawer_on_cash_payment
                                ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
                                : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'
                                }`}
                            >
                              {posForm.open_drawer_on_cash_payment ? t('common.enabled') : t('common.disabled')}
                            </button>
                          </div>
                        ),
                      },
                      {
                        id: 'open_drawer_on_cash_movement',
                        title: t('settings.openDrawerOnCashMovement'),
                        description: t('settings.openDrawerOnCashMovementHelp'),
                        keywords: ['cash movement', 'cash in', 'cash out', 'drawer movement', 'open drawer'],
                        render: () => (
                          <div key="open_drawer_on_cash_movement" className="flex items-center justify-between py-3">
                            <div>
                              <p className="font-medium text-gray-900 text-sm">{t('settings.openDrawerOnCashMovement')}</p>
                              <p className="text-xs text-gray-500">{t('settings.openDrawerOnCashMovementHelp')}</p>
                            </div>
                            <button
                              type="button"
                              onClick={() => setPosForm({ ...posForm, open_drawer_on_cash_movement: !posForm.open_drawer_on_cash_movement })}
                              className={`px-4 py-1.5 text-xs font-semibold rounded-full border transition-all select-none ${posForm.open_drawer_on_cash_movement
                                ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
                                : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'
                                }`}
                            >
                              {posForm.open_drawer_on_cash_movement ? t('common.enabled') : t('common.disabled')}
                            </button>
                          </div>
                        ),
                      },
                      {
                        id: 'daily_sales_summary',
                        title: t('settings.dailySalesSummary'),
                        description: `${t('settings.dailySalesSummaryHelp')} ${t('settings.sendAtHelp')}`,
                        keywords: ['daily sales summary', 'summary email', 'sales report', 'daily report', 'send at', 'summary hour'],
                        render: () => (
                          <div key="daily_sales_summary" className="py-3 space-y-3">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-medium text-gray-900 text-sm">{t('settings.dailySalesSummary')}</p>
                                <p className="text-xs text-gray-500">{t('settings.dailySalesSummaryHelp')}</p>
                              </div>
                              <button
                                type="button"
                                onClick={() => setPosForm({ ...posForm, daily_sales_summary: !posForm.daily_sales_summary })}
                                className={`px-4 py-1.5 text-xs font-semibold rounded-full border transition-all select-none ${posForm.daily_sales_summary
                                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
                                  : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'
                                  }`}
                              >
                                {posForm.daily_sales_summary ? t('common.enabled') : t('common.disabled')}
                              </button>
                            </div>
                            {posForm.daily_sales_summary && (
                              <div className="flex items-center justify-between py-2 pl-3 bg-gray-50 rounded-xl border border-gray-200/60">
                                <div>
                                  <p className="font-medium text-gray-900 text-xs">{t('settings.sendAt')}</p>
                                  <p className="text-[11px] text-gray-500">{t('settings.sendAtHelp')}</p>
                                </div>
                                <select
                                  className="input-field w-28 text-sm mr-2"
                                  value={posForm.daily_sales_summary_hour ?? 20}
                                  onChange={(e) =>
                                    setPosForm({
                                      ...posForm,
                                      daily_sales_summary_hour: parseInt(e.target.value, 10),
                                    })
                                  }
                                >
                                  {Array.from({ length: 24 }, (_, h) => (
                                    <option key={h} value={h}>
                                      {h}:00
                                    </option>
                                  ))}
                                </select>
                              </div>
                            )}
                          </div>
                        ),
                      },
                    ],
                  },
                ];

                const filteredSections = sections
                  .filter((section) => posRulesCategory === 'all' || posRulesCategory === section.id || query.length > 0)
                  .map((section) => {
                    const matchingItems = section.items.filter((item) => {
                      if (!query) return true;
                      return (
                        item.title.toLowerCase().includes(query) ||
                        item.description.toLowerCase().includes(query) ||
                        item.keywords.some((kw) => kw.toLowerCase().includes(query))
                      );
                    });
                    return { ...section, items: matchingItems };
                  })
                  .filter((section) => section.items.length > 0);

                const totalMatchedItems = filteredSections.reduce((acc, s) => acc + s.items.length, 0);

                return (
                  <div className="space-y-6">
                    {/* Header with Save Button */}
                    <div className="card p-6">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-5">
                        <div>
                          <h2 className="text-lg font-semibold text-gray-900 mb-1">{t('settings.posRulesTitle')}</h2>
                          <p className="text-sm text-gray-500">
                            {t('settings.posRulesSubtitle')}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => handlePosSettingsSave()}
                          disabled={updatePosSettingsMutation.isPending}
                          className="btn-primary flex items-center gap-2 self-start sm:self-auto shrink-0 shadow-sm"
                        >
                          {updatePosSettingsMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                          {t('settings.savePosRules')}
                        </button>
                      </div>

                      {/* Search Box */}
                      <div className="relative mb-4">
                        <div className="relative flex items-center">
                          <Search className="w-4 h-4 text-gray-400 absolute left-3.5 pointer-events-none" />
                          <input
                            type="text"
                            value={posRulesSearch}
                            onChange={(e) => setPosRulesSearch(e.target.value)}
                            placeholder="Search POS settings (e.g. delivery, tax, discount, shift, drawer, barcode)..."
                            className="w-full pl-10 pr-10 py-2.5 bg-gray-50 hover:bg-white focus:bg-white border border-gray-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-100 rounded-xl text-sm transition-all outline-none"
                          />
                          {posRulesSearch && (
                            <button
                              type="button"
                              onClick={() => setPosRulesSearch('')}
                              className="absolute right-3 p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors"
                              title="Clear search"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                        {posRulesSearch.trim() && (
                          <div className="flex items-center justify-between text-xs text-gray-500 mt-2 px-1">
                            <span>
                              Found <strong className="text-gray-900">{totalMatchedItems}</strong> {totalMatchedItems === 1 ? 'setting' : 'settings'} matching &ldquo;{posRulesSearch}&rdquo;
                            </span>
                            <button
                              type="button"
                              onClick={() => { setPosRulesSearch(''); setPosRulesCategory('all'); }}
                              className="text-primary-600 hover:underline font-medium"
                            >
                              Clear filter
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Category Filter Pills */}
                      <div className="flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-none">
                        {categoryChips.map((chip) => (
                          <button
                            key={chip.id}
                            type="button"
                            onClick={() => setPosRulesCategory(chip.id as any)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${posRulesCategory === chip.id
                              ? 'bg-primary-600 text-white shadow-xs'
                              : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                              }`}
                          >
                            {chip.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Filtered Sections Form */}
                    <form onSubmit={handlePosSettingsSave} className="space-y-5">
                      {filteredSections.length === 0 ? (
                        <div className="card text-center py-12 px-6">
                          <Search className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                          <h3 className="text-base font-semibold text-gray-800">No settings found</h3>
                          <p className="text-sm text-gray-500 mt-1 max-w-sm mx-auto">
                            We couldn&apos;t find any setting matching &ldquo;<span className="font-medium text-gray-700">{posRulesSearch}</span>&rdquo;.
                          </p>
                          <button
                            type="button"
                            onClick={() => { setPosRulesSearch(''); setPosRulesCategory('all'); }}
                            className="btn-secondary text-xs mt-4"
                          >
                            Clear Search Filter
                          </button>
                        </div>
                      ) : (
                        filteredSections.map((section) => {
                          const Icon = section.icon;
                          return (
                            <div key={section.id} className="card overflow-hidden">
                              <div className="px-5 py-3.5 bg-gray-50/70 border-b border-gray-100 flex items-center justify-between">
                                <div className="flex items-center gap-2.5">
                                  <div className={`p-1.5 rounded-lg border ${section.color}`}>
                                    <Icon className="w-4 h-4" />
                                  </div>
                                  <div>
                                    <h3 className="text-sm font-semibold text-gray-900">{section.title}</h3>
                                    <p className="text-xs text-gray-500">{section.subtitle}</p>
                                  </div>
                                </div>
                                <span className="text-[11px] font-medium text-gray-400 bg-white border border-gray-200 px-2 py-0.5 rounded-full">
                                  {section.items.length} {section.items.length === 1 ? 'option' : 'options'}
                                </span>
                              </div>
                              <div className="p-5 divide-y divide-gray-100">
                                {section.items.map((item) => item.render())}
                              </div>
                            </div>
                          );
                        })
                      )}

                      {/* Bottom Save & Status Bar */}
                      <div className="card p-4 flex flex-col sm:flex-row items-center justify-between gap-3">
                        <div className="flex items-center gap-2 min-h-[24px]">
                          {posMessage && (
                            <div className="flex items-center gap-2 text-emerald-700 text-sm font-medium animate-in fade-in">
                              <CheckCircle className="w-4 h-4 shrink-0" /> {posMessage}
                            </div>
                          )}
                          {posError && (
                            <div className="flex items-center gap-2 text-red-600 text-sm font-medium animate-in fade-in">
                              <AlertTriangle className="w-4 h-4 shrink-0" /> {posError}
                            </div>
                          )}
                        </div>
                        <button
                          type="submit"
                          disabled={updatePosSettingsMutation.isPending}
                          className="btn-primary flex items-center gap-2 shrink-0 shadow-sm"
                        >
                          {updatePosSettingsMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                          {t('settings.savePosRules')}
                        </button>
                      </div>
                    </form>
                  </div>
                );
              })()}

              {activeTab === 'pos_sync' && (
                <div className="card p-6">
                  <PosSyncModeTab
                    posForm={posForm}
                    setPosForm={setPosForm}
                    setPosError={setPosError}
                    onSave={handlePosSettingsSave}
                    isSaving={updatePosSettingsMutation.isPending}
                    message={posMessage}
                    error={posError}
                    companyName={companyForm.company_name || company?.name}
                    companyLogo={companyForm.company_logo}
                  />
                </div>
              )}

              {/* FBR Digital Invoice Integration — Pakistan only */}
              {activeTab === 'fbr' && isPakistan(settingsCountry) && (
                <div className="card p-6">
                  <FBRIntegrationTab />
                </div>
              )}

              {activeTab === 'zatca' && isSaudiArabia(settingsCountry) && (
                <div className="card p-6">
                  <ZATCAIntegrationTab />
                </div>
              )}

              {activeTab === 'uae' && isUae(settingsCountry) && (
                <div className="card p-6">
                  <UAEIntegrationTab />
                </div>
              )}

              {activeTab === 'marketplaces' && (
                <div className="card p-6 min-w-0 overflow-x-hidden">
                  <MarketplaceChannelsTab />
                </div>
              )}

              {activeTab === 'marketing' && (
                <div className="card p-6 min-w-0 overflow-x-hidden">
                  <MarketingHubTab />
                </div>
              )}

              {/* Receipt Template Designer */}
              {activeTab === 'receipt_designer' && (
                <div className="card p-6">
                  <div className="flex justify-between items-center mb-6">
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">{t('settings.receiptDesignerTitle')}</h2>
                      <p className="text-sm text-gray-500">{t('settings.receiptDesignerHelp')}</p>
                    </div>
                    <button
                      onClick={handlePosSettingsSave}
                      disabled={updatePosSettingsMutation.isPending}
                      className="btn-primary flex items-center gap-2"
                    >
                      {updatePosSettingsMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      {t('settings.saveTemplate')}
                    </button>
                  </div>

                  {posMessage && (
                    <div className="mb-4 bg-emerald-50 text-emerald-800 text-sm px-4 py-2.5 rounded-lg flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" /> {posMessage}
                    </div>
                  )}
                  {posError && (
                    <div className="mb-4 bg-red-50 text-red-800 text-sm px-4 py-2.5 rounded-lg flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" /> {posError}
                    </div>
                  )}

                  <ReceiptTemplateDesigner
                    settings={posForm.receipt_template_settings}
                    onChange={(newSettings) => setPosForm({ ...posForm, receipt_template_settings: newSettings })}
                  />
                </div>
              )}

              {/* Data Management */}
              {activeTab === 'data' && (
                <div className="space-y-4">
                  <div className="card p-6 mb-4 border border-blue-200 bg-blue-50">
                    <h2 className="text-lg font-semibold text-gray-900 mb-1">Test Daily Sales Report</h2>
                    <p className="text-sm text-gray-500 mb-4">Click to instantly generate and send the daily sales report email for today.</p>
                    <button
                      onClick={async () => {
                        setTestingEmail(true);
                        try {
                          await api.get('/method/saas_pos.api.reports.test_send_daily_sales_summary');
                          toast.success('Test email sent successfully!');
                        } catch (e) {
                          toast.error('Failed to send test email');
                        }
                        setTestingEmail(false);
                      }}
                      disabled={testingEmail}
                      className="btn-primary flex items-center gap-2"
                    >
                      {testingEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                      Send Test Email
                    </button>
                  </div>

                  <div className="card p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-1">{t('settings.sampleDataTitle')}</h2>
                    <p className="text-sm text-gray-500 mb-5">
                      {t('settings.sampleDataHelp')}
                    </p>

                    {/* Load button */}
                    <button
                      onClick={() => loadSampleDataMutation.mutate()}
                      disabled={loadSampleDataMutation.isPending || profile?.is_sample_data_loaded}
                      className="btn-secondary flex items-center gap-2 mb-4">
                      {loadSampleDataMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Database className="w-4 h-4" />}
                      {profile?.is_sample_data_loaded ? t('settings.sampleDataLoaded') : t('settings.loadSampleData')}
                    </button>

                    {/* Remove sample data — only shown when loaded */}
                    {!!profile?.is_sample_data_loaded && (
                      <div className="border border-red-100 rounded-xl p-4 bg-red-50">
                        <div className="flex items-start gap-3 mb-3">
                          <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                          <div>
                            <p className="text-sm font-semibold text-red-800">{t('settings.removeSampleData')}</p>
                            <p className="text-xs text-red-600 mt-0.5">
                              {t('settings.removeSampleDataHelp')}
                            </p>
                          </div>
                        </div>

                        {!showDeleteConfirm ? (
                          <button
                            onClick={() => setShowDeleteConfirm(true)}
                            className="flex items-center gap-2 px-4 py-2 bg-white border border-red-300 text-red-700 rounded-lg text-sm font-medium hover:bg-red-100 transition-colors">
                            <Trash2 className="w-4 h-4" />
                            {t('settings.removeSampleData')}
                          </button>
                        ) : (
                          <div className="flex items-center gap-3">
                            <button
                              onClick={() => deleteSampleDataMutation.mutate()}
                              disabled={deleteSampleDataMutation.isPending}
                              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors">
                              {deleteSampleDataMutation.isPending
                                ? <Loader2 className="w-4 h-4 animate-spin" />
                                : <Trash2 className="w-4 h-4" />}
                              {t('settings.yesDeleteNow')}
                            </button>
                            <button
                              onClick={() => setShowDeleteConfirm(false)}
                              className="px-4 py-2 text-sm text-gray-600 hover:underline">
                              {t('common.cancel')}
                            </button>
                          </div>
                        )}

                        {deleteSampleDataMutation.isSuccess && (
                          <div className="flex items-center gap-2 mt-3 text-emerald-700 text-sm">
                            <CheckCircle className="w-4 h-4" />
                            {t('settings.sampleDataRemoved')}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Clear Sales Data */}
                  <div className="card p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-1">{t('settings.clearSalesDataTitle', 'Clear Sales Data')}</h2>
                    <p className="text-sm text-gray-500 mb-5">
                      {t('settings.clearSalesDataHelp', 'Permanently delete all sales data (invoices, returns, shifts, sales cash ledgers) and revert inventory stock to zero sales impact. This is irreversible.')}
                    </p>

                    <div className="border border-red-200 rounded-xl p-4 bg-red-50">
                      <div className="flex items-start gap-3 mb-3">
                        <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-semibold text-red-800">{t('settings.clearSalesDataWarning', 'Clear all sales data & revert stock')}</p>
                          <p className="text-xs text-red-600 mt-0.5">
                            {t('settings.clearSalesDataSubtext', 'This action cannot be undone. It will remove all invoices, cancel sales payments/ledgers, and restore all inventory stock levels.')}
                          </p>
                        </div>
                      </div>

                      <button
                        onClick={() => setShowClearDataModal(true)}
                        className="flex items-center gap-2 px-4 py-2 bg-white border border-red-300 text-red-700 rounded-lg text-sm font-medium hover:bg-red-100 transition-colors">
                        <Trash2 className="w-4 h-4" />
                        {t('settings.clearSalesDataBtn', 'Clear Sales Data')}
                      </button>
                    </div>
                  </div>

                  {/* Factory Reset hidden as requested
                  <div className="card p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-1">{t('settings.factoryResetTitle')}</h2>
                    <p className="text-sm text-gray-500 mb-5">
                      {t('settings.factoryResetHelp')}
                    </p>

                    <div className="border border-red-200 rounded-xl p-4 bg-red-50">
                      <div className="flex items-start gap-3 mb-3">
                        <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-semibold text-red-800">{t('settings.deleteAllData')}</p>
                          <p className="text-xs text-red-600 mt-0.5">
                            {t('settings.factoryResetWarning')}
                          </p>
                        </div>
                      </div>

                      {!showFactoryResetConfirm ? (
                        <button
                          onClick={() => setShowFactoryResetConfirm(true)}
                          disabled={factoryResetMutation.isPending}
                          className="flex items-center gap-2 px-4 py-2 bg-white border border-red-300 text-red-700 rounded-lg text-sm font-medium hover:bg-red-100 transition-colors">
                          <Trash2 className="w-4 h-4" />
                          {t('settings.factoryReset')}
                        </button>
                      ) : (
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => factoryResetMutation.mutate()}
                            disabled={factoryResetMutation.isPending}
                            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors">
                            {factoryResetMutation.isPending
                              ? <Loader2 className="w-4 h-4 animate-spin" />
                              : <Trash2 className="w-4 h-4" />}
                            {t('settings.yesResetEverything')}
                          </button>
                          <button
                            onClick={() => setShowFactoryResetConfirm(false)}
                            disabled={factoryResetMutation.isPending}
                            className="px-4 py-2 text-sm text-gray-600 hover:underline">
                            {t('common.cancel')}
                          </button>
                        </div>
                      )}

                      {factoryResetMutation.isSuccess && (
                        <div className="flex items-center gap-2 mt-3 text-emerald-700 text-sm">
                          <CheckCircle className="w-4 h-4" />
                          {t('settings.factoryResetSuccess')}
                        </div>
                      )}
                      {factoryResetMutation.isError && (
                        <div className="flex items-center gap-2 mt-3 text-red-700 text-sm">
                          <AlertTriangle className="w-4 h-4" />
                          {(factoryResetMutation.error as { response?: { data?: { message?: string } }; message?: string })
                            ?.response?.data?.message
                            || (factoryResetMutation.error as { message?: string })?.message
                            || t('settings.factoryResetFailed')}
                        </div>
                      )}
                    </div>
                  </div>
                  */}
                </div>
              )}

              {/* Starter Catalog */}
              {activeTab === 'starter_catalog' && isCompanyAdminUser && (
                <StarterCatalogTab />
              )}

              {/* Privacy / GDPR tools */}
              {activeTab === 'privacy' && isCompanyAdminUser && (<div className={`card p-6 space-y-5 ${isEuOrUk(companyForm.country) ? 'ring-1 ring-emerald-200' : ''}`}>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">{t('settings.gdpr')}</h2>
                  <p className="text-sm text-gray-500 mt-1">{t('settings.gdprHelp')}</p>
                  {companyForm.privacy_policy_url ? (
                    <a
                      href={companyForm.privacy_policy_url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-sm text-primary-600 hover:underline mt-2 inline-block"
                    >
                      {t('common.privacy')}
                    </a>
                  ) : isEuOrUk(companyForm.country) ? (
                    <p className="text-xs text-amber-700 mt-2 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                      Add a Privacy Policy URL in Company settings. Until then, customers may request export or anonymization via this screen.
                    </p>
                  ) : null}
                </div>

                <div className="flex flex-wrap gap-3">
                  <button
                    type="button"
                    disabled={privacyBusy}
                    className="btn-secondary flex items-center gap-2"
                    onClick={async () => {
                      setPrivacyBusy(true);
                      try {
                        const res = await privacyApi.exportCustomerData('csv');
                        const msg = res.data.message as { content: string; filename: string };
                        const blob = new Blob([msg.content], { type: 'text/csv;charset=utf-8' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = msg.filename || 'customers.csv';
                        a.click();
                        URL.revokeObjectURL(url);
                        toast.success('Customer export downloaded');
                      } catch (err) {
                        toast.error(getApiErrorMessage(err) || 'Export failed');
                      } finally {
                        setPrivacyBusy(false);
                      }
                    }}
                  >
                    {privacyBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                    {t('settings.exportCustomers')} (CSV)
                  </button>
                  <button
                    type="button"
                    disabled={privacyBusy}
                    className="btn-secondary flex items-center gap-2"
                    onClick={async () => {
                      setPrivacyBusy(true);
                      try {
                        const res = await privacyApi.exportCustomerData('json');
                        const msg = res.data.message as { content: string; filename: string };
                        const blob = new Blob([msg.content], { type: 'application/json' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = msg.filename || 'customers.json';
                        a.click();
                        URL.revokeObjectURL(url);
                        toast.success('Customer export downloaded');
                      } catch (err) {
                        toast.error(getApiErrorMessage(err) || 'Export failed');
                      } finally {
                        setPrivacyBusy(false);
                      }
                    }}
                  >
                    <Download className="w-4 h-4" />
                    {t('settings.exportCustomers')} (JSON)
                  </button>
                </div>

                <div className="border-t border-gray-100 pt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('settings.anonymizeCustomer')}
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    Enter the customer ID (document name). Clears mobile, email, address and tax IDs; invoices keep an anonymized party name.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <input
                      className="input-field flex-1"
                      placeholder="Customer ID"
                      value={anonymizeId}
                      onChange={(e) => setAnonymizeId(e.target.value)}
                    />
                    <button
                      type="button"
                      disabled={privacyBusy || !anonymizeId.trim()}
                      className="btn-secondary text-red-700 border-red-200"
                      onClick={() => setShowAnonymizeConfirm(true)}
                    >
                      {t('common.anonymize')}
                    </button>
                  </div>
                </div>
              </div>
              )}

              {/* Cash Pockets */}
              {activeTab === 'cash_pockets' && isCompanyAdminUser && <CashPocketsTab />}
              {activeTab === 'card_machines' && isCompanyAdminUser && <CardMachinesTab />}

              {/* Banks */}
              {activeTab === 'banks' && isCompanyAdminUser && <BanksTab />}

              {/* Brands */}
              {activeTab === 'brands' && isCompanyAdminUser && <BrandsTab />}

              {/* Models */}
              {activeTab === 'models' && isCompanyAdminUser && <ModelsTab />}

              {/* Printer Settings */}
              {activeTab === 'printer' && (
                <div className="card">
                  <PrinterSettingsPage />
                </div>
              )}

              {/* Weighing Scale */}
              {activeTab === 'scale' && (
                <div className="card">
                  <ScaleSettingsPage />
                </div>
              )}

              {/* Subscription */}
              {activeTab === 'subscription' && (
                <div className="space-y-4">
                  <div className="card p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Subscription Overview</h2>

                    <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl border border-gray-100 mb-6">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${status === 'Active' ? 'bg-emerald-100 text-emerald-600' :
                        status === 'Trial Active' ? 'bg-blue-100 text-blue-600' :
                          status === 'Trial Expired' ? 'bg-red-100 text-red-600' :
                            'bg-amber-100 text-amber-600'
                        }`}>
                        {status === 'Active' ? <Shield className="w-6 h-6" /> :
                          status === 'Trial Active' ? <Clock className="w-6 h-6" /> :
                            <AlertTriangle className="w-6 h-6" />}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900">
                          {status === 'Active' ? 'Active Subscription' :
                            status === 'Trial Active' ? `Trial Active — ${daysRemaining} days remaining` :
                              status === 'Trial Expired' ? 'Trial Expired — Action Required' :
                                'Subscription Status'}
                        </h3>
                        <p className="text-sm text-gray-500">
                          {status === 'Active' ? `Current plan: ${profile?.package?.package_name || 'Active'}` :
                            status === 'Trial Active' ? 'Your trial gives you full access to all features' :
                              status === 'Trial Expired' ? 'Your trial has ended. Please subscribe to continue.' :
                                'Check your subscription details'}
                        </p>
                      </div>
                    </div>

                    <div className="flex justify-end">
                      <Link to="/support" className="btn-primary flex items-center gap-2">
                        <CreditCard className="w-4 h-4" />
                        Manage Subscription & Plans
                      </Link>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {showClearDataModal && (
        <ClearDataModal onClose={() => setShowClearDataModal(false)} />
      )}
      {showAnonymizeConfirm && (
        <ConfirmModal
          title={t('settings.anonymizeCustomer', 'Anonymize Customer')}
          message="Are you sure you want to anonymize this customer’s personal data? This cannot be undone."
          confirmText={t('common.anonymize', 'Anonymize')}
          cancelText={t('common.cancel', 'Cancel')}
          isPending={privacyBusy}
          onConfirm={async () => {
            setPrivacyBusy(true);
            try {
              await privacyApi.anonymizeCustomer(anonymizeId.trim());
              toast.success('Customer anonymized');
              setAnonymizeId('');
              qc.invalidateQueries({ queryKey: ['customers'] });
              setShowAnonymizeConfirm(false);
            } catch (err) {
              toast.error(getApiErrorMessage(err) || 'Anonymize failed');
            } finally {
              setPrivacyBusy(false);
            }
          }}
          onClose={() => {
            if (!privacyBusy) setShowAnonymizeConfirm(false);
          }}
          type="danger"
        />
      )}
    </div>
  );
}
